import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
    FEATURE_COMING_SOON,
    FEATURE_ENABLED,
    GESTAO_FEATURE,
    GESTAO_PERMISSIONS,
    getGestaoState,
    getUser,
    hasPermission,
    hasPlanFeature,
    logout,
    refreshPlan,
} from "../../services/auth";
import api from "../../services/api";
import { subscribePendingBudgets, getPendingBudgetsCount } from "../../services/pendingBudgets";
import { subscribeCredits, getCredits } from "../../services/credits";
import { GESTAO_SETTINGS_TABS, firstGestaoSettingsPath } from "../GestaoSettingsTabs";
import { SETTINGS_PATHS, canSeeSettingsHub } from "../../pages/Settings/Hub/sections";
import Icon from "../Icon";
import CreditCoin from "../CreditCoin";
import Avatar from "../Avatar";
import NotificationBell from "../NotificationBell";
import ModalMyProfile from "../ModalMyProfile";
import { PlanFeatureUpgradeModal } from "../PlanFeatureUpgradeModal";

import Intercom from "@intercom/messenger-js-sdk";

const KNOWLEDGE_BASE_URL = 'https://ajuda.brinde.me/t/base-de-conhecimento';
const COLLAPSED_STORAGE_KEY = 'sidebar.collapsed';
const DESKTOP_QUERY = '(min-width: 1025px)';

/**
 * Um item também fica ativo nas rotas irmãs que ele declara em `matches` —
 * é o caso das notas de entrada, em que a aba da SEFAZ é outra rota e o menu
 * ficaria sem marcação nenhuma.
 */
const pathMatches = (pathname, entry) =>
    (entry.to && entry.to !== '/' && (pathname === entry.to || pathname.startsWith(entry.to + '/')))
    || (entry.matches ?? []).some(path => pathname === path || pathname.startsWith(path + '/'));

const isItemActive = (pathname, item) =>
    pathMatches(pathname, item) || (item.subitems ?? []).some(subitem => pathMatches(pathname, subitem));

const readCollapsed = () => {
    try {
        return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
    } catch (error) {
        return false;
    }
};

const useIsDesktop = () => {
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);

    useEffect(() => {
        const query = window.matchMedia(DESKTOP_QUERY);
        const update = () => setIsDesktop(query.matches);

        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, []);

    return isDesktop;
};

// Recolhida, as letras somem em cascata e o ponto desliza até o "b": o
// logo inteiro não cabe na barra.
const BrandLogo = () => (
    <svg className="logo-svg" viewBox="0 0 329.34 53.15" aria-hidden="true">
        <path className="w" d="M38.96,23.25c-.45-1.23-1-2.4-1.65-3.5-.65-1.11-1.41-2.15-2.26-3.12-.85-.97-1.8-1.84-2.86-2.61-1.07-.77-2.24-1.45-3.51-2.02-1.25-.56-2.58-.98-3.99-1.27-1.42-.29-2.93-.44-4.52-.45-1.59.01-3.1.16-4.52.45-1.42.29-2.75.71-3.99,1.27-.25.11-.52.24-.8.38-.28.14-.54.27-.77.4l-.81.45V0H0v32.47c0,1.29.11,2.57.34,3.83.23,1.26.56,2.51,1.01,3.74.45,1.23,1.01,2.4,1.66,3.51.66,1.11,1.42,2.16,2.28,3.14.86.98,1.83,1.86,2.91,2.65,1.09.79,2.28,1.47,3.58,2.06,1.21.54,2.52.96,3.91,1.25,1.4.29,2.88.46,4.45.49,1.57-.04,3.06-.2,4.45-.49,1.39-.29,2.7-.71,3.91-1.25,1.3-.58,2.49-1.27,3.58-2.06,1.08-.78,2.05-1.67,2.91-2.65.86-.98,1.62-2.03,2.28-3.14.66-1.11,1.21-2.28,1.66-3.51.45-1.23.79-2.48,1.02-3.74.22-1.26.34-2.54.34-3.83v-1.5c0-1.35-.11-2.66-.34-3.95-.23-1.29-.57-2.55-1.02-3.78ZM29.96,35.33c-.18,1.11-.45,2.13-.82,3.08-.37.96-.85,1.82-1.43,2.57-.59.75-1.28,1.4-2.08,1.93-.73.48-1.55.86-2.46,1.12-.9.26-1.9.41-2.99.45h-.04c-1.09-.04-2.09-.2-2.99-.45-.91-.26-1.74-.64-2.46-1.12-.8-.53-1.49-1.18-2.08-1.93-.59-.75-1.06-1.61-1.43-2.57-.72-1.89-1.09-4.09-1.09-6.61,0-1.35.1-2.6.29-3.76.19-1.16.49-2.21.88-3.16.39-.96.88-1.81,1.46-2.54.59-.74,1.27-1.37,2.04-1.87.74-.49,1.57-.86,2.48-1.11.89-.25,1.86-.38,2.91-.4h.02c1.05.02,2.02.15,2.91.4.91.25,1.73.62,2.48,1.11.77.51,1.45,1.13,2.04,1.87.58.73,1.07,1.58,1.46,2.54.39.95.68,2.01.88,3.16.19,1.15.29,2.41.29,3.76,0,1.25-.09,2.42-.27,3.53Z"/>
        <g className="letter" style={{ '--i': 6 }}><path className="w" d="M55.63,11.98c-1.17.57-2.25,1.24-3.22,2.01-.97.77-1.85,1.64-2.63,2.6-.79.98-1.48,2.02-2.08,3.12-.6,1.1-1.11,2.27-1.52,3.5-.42,1.24-.73,2.5-.94,3.79-.21,1.28-.31,2.6-.31,3.96v20.92h9.2v-20.17c0-1.34.1-2.57.27-3.7.18-1.15.45-2.19.8-3.12.36-.96.81-1.8,1.35-2.54.54-.74,1.17-1.37,1.89-1.88.69-.49,1.46-.87,2.3-1.12.67-.21,1.4-.33,2.17-.38v-8.68c-1.26.04-2.46.19-3.6.44-1.3.28-2.52.7-3.66,1.26Z"/></g>
        <g className="letter" style={{ '--i': 5 }}><polygon className="w" points="69.68 42.56 69.68 51.88 76.43 51.88 76.46 51.88 78.88 51.88 78.88 48.02 78.88 47.97 78.88 42.56 78.88 27.04 69.68 27.04 69.68 42.56"/><path className="c" d="M74.28,9c-1.55,0-2.96.63-3.98,1.65-1.02,1.02-1.65,2.42-1.65,3.98s.63,2.96,1.65,3.98,2.42,1.65,3.98,1.65,2.96-.63,3.98-1.65c1.02-1.02,1.65-2.42,1.65-3.98s-.63-2.96-1.65-3.98c-1.02-1.02-2.42-1.65-3.98-1.65Z"/></g>
        <g className="letter" style={{ '--i': 4 }}><path className="w" d="M123.87,23.23c-.42-1.23-.92-2.4-1.52-3.5-.6-1.1-1.29-2.14-2.08-3.12-.78-.97-1.66-1.84-2.63-2.6-.98-.77-2.05-1.44-3.22-2.01-1.14-.55-2.36-.97-3.66-1.26-1.31-.29-2.69-.44-4.15-.45-1.45.01-2.84.16-4.15.45-1.3.28-2.52.7-3.66,1.26-1.17.57-2.25,1.24-3.22,2.01-.97.77-1.85,1.64-2.63,2.6-.79.98-1.48,2.02-2.08,3.12-.6,1.1-1.11,2.27-1.52,3.5-.42,1.24-.73,2.5-.94,3.79-.21,1.28-.31,2.6-.31,3.96v20.91h9.2v-20.16c0-1.34.1-2.57.27-3.7.18-1.15.45-2.19.8-3.12.36-.96.81-1.8,1.35-2.54.54-.74,1.17-1.37,1.89-1.88.69-.49,1.46-.87,2.3-1.12.82-.25,1.73-.39,2.7-.4h.02c.98.02,1.88.15,2.7.4.84.26,1.61.63,2.3,1.12.71.51,1.34,1.13,1.89,1.88.54.73.99,1.58,1.35,2.54.35.94.62,1.98.8,3.12.18,1.13.27,2.37.27,3.7v11.26s0,8.9,0,8.9h9.2v-20.91c0-1.36-.1-2.68-.31-3.96-.21-1.29-.52-2.55-.94-3.79Z"/></g>
        <g className="letter" style={{ '--i': 3 }}><path className="w" d="M163.19,0h-2.42v13.23l-.81-.45c-.25-.14-.5-.27-.77-.4-.26-.13-.53-.26-.8-.38-1.25-.56-2.58-.98-3.99-1.27-1.42-.29-2.93-.44-4.52-.45-1.59.01-3.1.16-4.52.45-1.42.29-2.75.71-3.99,1.27-1.27.57-2.45,1.24-3.51,2.02-1.06.77-2.02,1.64-2.86,2.61-.85.97-1.6,2.01-2.26,3.12-.65,1.1-1.2,2.27-1.66,3.5-.45,1.23-.79,2.49-1.01,3.78-.23,1.29-.34,2.6-.34,3.95v1.5c0,1.29.11,2.57.34,3.83.23,1.26.56,2.51,1.01,3.74.45,1.23,1.01,2.4,1.66,3.51.66,1.11,1.42,2.16,2.28,3.14.86.98,1.83,1.86,2.91,2.65,1.09.79,2.28,1.47,3.58,2.06,1.21.54,2.52.96,3.91,1.25,1.4.29,2.88.46,4.45.49,1.57-.04,3.06-.2,4.45-.49,1.39-.29,2.7-.71,3.91-1.25,1.3-.58,2.49-1.27,3.58-2.06,1.08-.78,2.05-1.67,2.91-2.65.86-.98,1.62-2.03,2.28-3.14.66-1.11,1.21-2.28,1.66-3.51.45-1.23.79-2.48,1.02-3.74.22-1.26.34-2.54.34-3.83V0h-6.84ZM158.86,38.41c-.37.96-.85,1.82-1.43,2.57-.59.75-1.28,1.4-2.08,1.93-.73.48-1.55.86-2.46,1.12-.9.26-1.9.41-2.99.45h-.04c-1.09-.04-2.09-.2-2.99-.45-.91-.26-1.74-.64-2.46-1.12-.8-.53-1.49-1.18-2.08-1.93-.59-.75-1.06-1.61-1.43-2.57-.36-.94-.63-1.97-.82-3.08-.18-1.1-.27-2.28-.27-3.53,0-1.35.1-2.6.29-3.76.19-1.16.49-2.21.88-3.16.39-.96.88-1.81,1.46-2.54.59-.74,1.27-1.37,2.04-1.87.74-.49,1.57-.86,2.48-1.11.89-.25,1.86-.38,2.91-.4h.02c1.05.02,2.02.15,2.91.4.91.25,1.73.62,2.48,1.11.77.51,1.45,1.13,2.04,1.87.58.73,1.07,1.58,1.46,2.54.39.95.68,2.01.88,3.16.19,1.15.29,2.41.29,3.76,0,2.52-.36,4.72-1.09,6.61Z"/></g>
        <g className="letter" style={{ '--i': 2 }}><path className="w" d="M213.62,23.25c-.45-1.23-1-2.4-1.66-3.5-.65-1.11-1.41-2.15-2.26-3.12-.85-.97-1.8-1.84-2.86-2.61-1.07-.77-2.24-1.45-3.51-2.02-1.25-.56-2.58-.98-3.99-1.27-1.42-.29-2.93-.44-4.52-.45h-.02c-1.59.01-3.1.16-4.52.45-1.42.29-2.75.71-3.99,1.27-1.27.57-2.45,1.24-3.51,2.02-1.06.77-2.02,1.64-2.86,2.61-.85.97-1.6,2.01-2.26,3.12-.65,1.1-1.2,2.27-1.66,3.5-.45,1.23-.79,2.49-1.02,3.78-.22,1.29-.34,2.6-.34,3.95v1.5c0,1.29.11,2.57.34,3.83.23,1.27.57,2.51,1.02,3.74.45,1.23,1.01,2.4,1.66,3.51.66,1.11,1.42,2.16,2.28,3.14.86.98,1.83,1.86,2.91,2.65,1.09.79,2.28,1.47,3.58,2.06,1.21.54,2.52.96,3.91,1.25,1.4.29,2.88.46,4.45.49h.02c1.57-.04,3.05-.2,4.45-.49,1.39-.29,2.7-.71,3.91-1.25,1.3-.58,2.49-1.27,3.58-2.06,1.08-.78,2.05-1.67,2.91-2.65.86-.98,1.62-2.03,2.28-3.14.32-.54.62-1.09.89-1.66h-11.31c-.38.37-.8.7-1.24,1-.73.48-1.55.86-2.46,1.12-.9.26-1.9.41-2.99.45h-.06c-1.09-.04-2.09-.2-2.99-.45-.91-.26-1.74-.64-2.46-1.12-.8-.53-1.49-1.18-2.08-1.93-.59-.75-1.06-1.61-1.43-2.57-.36-.94-.63-1.97-.82-3.08l-.03-.21h29.85c.11-.87.16-1.75.16-2.64v-1.5c0-1.35-.11-2.66-.34-3.95-.23-1.29-.56-2.55-1.02-3.78ZM184.97,28.33l.05-.28c.19-1.16.49-2.21.88-3.16.39-.96.88-1.81,1.46-2.54.59-.74,1.27-1.37,2.04-1.87.74-.49,1.57-.86,2.48-1.11.89-.25,1.86-.38,2.91-.4h.04c1.05.02,2.02.15,2.91.4.91.25,1.73.62,2.48,1.11.77.51,1.45,1.13,2.04,1.87.58.73,1.07,1.58,1.46,2.54.39.95.68,2.01.88,3.16l.05.28h-19.67Z"/></g>
        <g className="dot"><path className="w" d="M228.12,43.55c-1.02-1.02-2.42-1.65-3.98-1.65s-2.96.63-3.98,1.65c-1.02,1.02-1.65,2.42-1.65,3.98s.63,2.96,1.65,3.98c1.02,1.02,2.42,1.65,3.98,1.65s2.96-.63,3.98-1.65c1.02-1.02,1.65-2.42,1.65-3.98s-.63-2.96-1.65-3.98Z"/></g>
        <g className="letter" style={{ '--i': 1 }}><path className="c" d="M283.16,23.23c-.42-1.23-.92-2.4-1.52-3.5-.6-1.1-1.29-2.14-2.08-3.12-.78-.97-1.66-1.84-2.63-2.6-.98-.77-2.05-1.44-3.22-2.01-1.14-.55-2.36-.97-3.66-1.26-1.31-.29-2.69-.44-4.15-.45-1.45.01-2.84.16-4.15.45-1.3.28-2.52.7-3.66,1.26-1.17.57-2.25,1.24-3.22,2.01-.97.77-1.85,1.64-2.63,2.6-.08.1-.17.21-.25.32-.08-.11-.17-.21-.25-.32-.78-.97-1.66-1.84-2.63-2.6-.98-.77-2.05-1.44-3.22-2.01-1.14-.55-2.36-.97-3.66-1.26-1.31-.29-2.69-.44-4.15-.45-1.45.01-2.84.16-4.15.45-1.3.28-2.52.7-3.66,1.26-1.17.57-2.25,1.24-3.22,2.01-.97.77-1.85,1.64-2.63,2.6-.79.98-1.48,2.02-2.08,3.12-.6,1.1-1.11,2.27-1.52,3.5-.42,1.24-.73,2.5-.94,3.79-.21,1.28-.31,2.6-.31,3.96v4.14h9.2v-3.39c0-1.34.1-2.57.27-3.7.18-1.15.45-2.19.8-3.12.36-.96.81-1.8,1.35-2.54.54-.74,1.17-1.37,1.89-1.88.69-.49,1.46-.87,2.3-1.12.82-.25,1.73-.39,2.7-.4h.02c.98.02,1.88.15,2.7.4.84.26,1.61.63,2.3,1.12.71.51,1.34,1.13,1.89,1.88.54.73.99,1.58,1.35,2.54.35.94.62,1.98.8,3.12.18,1.13.27,2.37.27,3.7v10.18h9.2v-10.18c0-1.34.1-2.57.27-3.7.18-1.15.45-2.19.8-3.12.36-.96.81-1.8,1.35-2.54.54-.74,1.17-1.37,1.89-1.88.69-.49,1.46-.87,2.3-1.12.82-.25,1.73-.38,2.7-.4h.02c.98.02,1.88.15,2.7.4.84.25,1.61.63,2.3,1.12.71.51,1.34,1.13,1.89,1.88.54.73.99,1.58,1.35,2.54.35.94.62,1.98.8,3.12.18,1.13.27,2.37.27,3.7v20.16h9.2v-20.91c0-1.36-.1-2.68-.31-3.96-.21-1.29-.52-2.55-.94-3.79Z"/></g>
        <g className="letter" style={{ '--i': 0 }}><path className="c" d="M329.01,27.03c-.23-1.29-.57-2.55-1.02-3.78-.45-1.23-1-2.4-1.66-3.5-.65-1.11-1.41-2.15-2.26-3.12-.85-.97-1.8-1.84-2.86-2.61-1.07-.77-2.24-1.45-3.51-2.02-1.25-.56-2.58-.98-3.99-1.27-1.42-.29-2.93-.44-4.52-.45h-.02c-1.59.01-3.1.16-4.52.45-1.42.29-2.75.71-3.99,1.27-1.27.57-2.45,1.24-3.51,2.02-1.06.77-2.02,1.64-2.86,2.61-.85.97-1.6,2.01-2.26,3.12-.65,1.1-1.2,2.27-1.66,3.5-.45,1.23-.79,2.49-1.02,3.78-.22,1.29-.34,2.6-.34,3.95v1.5c0,1.29.11,2.57.34,3.83.23,1.27.57,2.51,1.02,3.74.45,1.23,1.01,2.4,1.66,3.51.66,1.11,1.42,2.16,2.28,3.14.86.98,1.83,1.86,2.91,2.65,1.09.79,2.28,1.47,3.58,2.06,1.21.54,2.52.96,3.91,1.25,1.4.29,2.88.46,4.45.49h.02c1.57-.04,3.06-.2,4.45-.49,1.39-.29,2.7-.71,3.91-1.25,1.3-.58,2.49-1.27,3.58-2.06,1.08-.78,2.05-1.67,2.91-2.65.86-.98,1.62-2.03,2.28-3.14.32-.54.62-1.09.89-1.66h-11.31c-.38.37-.8.7-1.24,1-.73.48-1.55.86-2.46,1.12-.9.26-1.9.41-2.99.45h-.06c-1.09-.04-2.09-.2-2.99-.45-.91-.26-1.74-.64-2.46-1.12-.8-.53-1.49-1.18-2.08-1.93-.59-.75-1.06-1.61-1.43-2.57-.36-.94-.63-1.97-.82-3.08l-.03-.21h29.85c.11-.87.16-1.75.16-2.64v-1.5c0-1.35-.11-2.66-.34-3.95ZM299.34,28.33l.05-.28c.19-1.16.49-2.21.88-3.16.39-.96.88-1.81,1.46-2.54.59-.74,1.27-1.37,2.04-1.87.74-.49,1.57-.86,2.48-1.11.89-.25,1.86-.38,2.91-.4h.04c1.05.02,2.02.15,2.91.4.91.25,1.73.62,2.48,1.11.77.51,1.45,1.13,2.04,1.87.58.73,1.07,1.58,1.46,2.54.39.95.68,2.01.88,3.16l.05.28h-19.67Z"/></g>
    </svg>
);

const ItemTrail = ({ item }) => (
    <>
        {item.alert !== undefined && item.alert !== 0 && <span className="alert">{item.alert}</span>}
        {item.new && !item.state && <span className="new">New</span>}
        {item.state === 'locked' && <Icon name="lock" className="lock" size={16} />}
        {item.subitems && <Icon name="arrow" className="arrow" size={16} />}
    </>
);

const SidebarItem = ({ item, open, onToggle, onNavigate, onHover, onLeave }) => {
    const { pathname } = useLocation();
    const active = isItemActive(pathname, item);

    const className = [
        'item',
        active ? 'active' : '',
        open ? 'open' : '',
        item.state === FEATURE_COMING_SOON ? 'soon' : '',
        item.state === 'locked' ? 'locked' : '',
    ].filter(Boolean).join(' ');

    const hoverProps = {
        onMouseEnter: event => onHover(item, event.currentTarget),
        onFocus: event => onHover(item, event.currentTarget),
        onMouseLeave: onLeave,
    };

    const content = (
        <>
            <Icon name={item.svg} />
            <span className="label">{item.name}</span>
            <span className="trail"><ItemTrail item={item} /></span>
        </>
    );

    if (item.state === FEATURE_COMING_SOON) {
        return (
            <li className={className}>
                <span className="link" aria-disabled="true" tabIndex={0} aria-label={`${item.name} (em breve)`} {...hoverProps}>
                    {content}
                </span>
            </li>
        );
    }

    if (item.state === 'locked') {
        return (
            <li className={className}>
                <button type="button" className="link" onClick={item.onLockedClick} aria-label={`${item.name} (não incluso no plano)`} {...hoverProps}>
                    {content}
                </button>
            </li>
        );
    }

    if (item.subitems) {
        return (
            <li className={className}>
                <button type="button" className="link" aria-expanded={open} onClick={() => onToggle(item)} {...hoverProps}>
                    {content}
                </button>
                <ul className="sub">
                    {item.subitems.map(subitem => (
                        <li key={subitem.to} className={pathMatches(pathname, subitem) ? 'active' : ''}>
                            <Link className="link" to={subitem.to} onClick={onNavigate}>
                                <span className="dot"></span>
                                {subitem.name}
                            </Link>
                        </li>
                    ))}
                </ul>
            </li>
        );
    }

    if (item.external) {
        return (
            <li className={className}>
                <a className="link" href={item.to} target="_blank" rel="noopener noreferrer" onClick={onNavigate} {...hoverProps}>
                    {content}
                </a>
            </li>
        );
    }

    return (
        <li className={className}>
            <Link className="link" to={item.to} onClick={onNavigate} {...hoverProps}>
                {content}
            </Link>
        </li>
    );
};

/**
 * Painel que aparece ao lado do ícone na barra recolhida: o grupo mostra os
 * subitens, o item simples só o nome (e o estado, se for o caso).
 */
const SidebarFlyout = ({ flyout, onNavigate, onEnter, onLeave }) => {
    const { pathname } = useLocation();

    if (!flyout) return null;

    const { item, top, left } = flyout;

    return (
        <div
            className={`sidebar-flyout${item.subitems ? '' : ' tip'}`}
            style={{ top, left }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            role={item.subitems ? 'menu' : 'tooltip'}
        >
            {item.subitems ? (
                <>
                    <p className="flyout-title">{item.name}</p>
                    <ul>
                        {item.subitems.map(subitem => (
                            <li key={subitem.to} className={pathMatches(pathname, subitem) ? 'active' : ''}>
                                <Link to={subitem.to} onClick={onNavigate} role="menuitem">
                                    <span className="dot"></span>
                                    {subitem.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </>
            ) : (
                <>
                    {item.name}
                    {item.alert !== undefined && item.alert !== 0 && <span className="alert">{item.alert}</span>}
                    {item.state === FEATURE_COMING_SOON && <span className="soon-tag">Em breve</span>}
                    {item.state === 'locked' && <Icon name="lock" size={14} />}
                </>
            )}
        </div>
    );
};

export const Sidebar = ({ mobileOpen = false, onClose }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isDesktop = useIsDesktop();

    const [user, setUserData] = useState(getUser());
    const [, setPlanVersion] = useState(0);

    const quoteCount = useSyncExternalStore(subscribePendingBudgets, getPendingBudgetsCount);
    const credits = useSyncExternalStore(subscribeCredits, getCredits);

    const [collapsedPreference, setCollapsedPreference] = useState(readCollapsed);
    const [openGroups, setOpenGroups] = useState(() => new Set());
    const [flyout, setFlyout] = useState(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [upgradeFeature, setUpgradeFeature] = useState(null);
    const userRef = useRef(null);
    const menuRef = useRef(null);
    const flyoutTimer = useRef(null);

    // A barra recolhida é só do desktop: no tablet/celular o menu é a gaveta.
    const collapsed = collapsedPreference && isDesktop;

    // Alimenta o --menu-fade do SCSS: altura do fade na base do menu quando há
    // itens abaixo da dobra, encolhendo até 0 conforme o scroll se aproxima do
    // fim (por isso valor contínuo em vez de classe liga/desliga).
    useEffect(() => {
        const menu = menuRef.current;
        if (!menu) return;

        const updateFade = () => {
            const remaining = menu.scrollHeight - menu.scrollTop - menu.clientHeight;
            menu.style.setProperty('--menu-fade', `${Math.max(0, Math.min(32, remaining))}px`);
        };

        const hide = () => setFlyout(null);

        updateFade();
        menu.addEventListener('scroll', updateFade, { passive: true });
        menu.addEventListener('scroll', hide, { passive: true });

        // Recalcula quando a viewport muda ou um submenu expande/colapsa.
        const observer = new ResizeObserver(updateFade);
        observer.observe(menu);
        if (menu.firstElementChild) observer.observe(menu.firstElementChild);

        return () => {
            menu.removeEventListener('scroll', updateFade);
            menu.removeEventListener('scroll', hide);
            observer.disconnect();
        };
    }, []);

    // O plano fica no navegador desde o login. Reconferir a cada carga é o que
    // faz um módulo liberado pelo super-admin aparecer sem novo login.
    useEffect(() => {
        api.get('/plan').then(res => {
            if (!res.data?.plan) return;

            refreshPlan(res.data.plan);
            setPlanVersion(version => version + 1);
        }).catch(() => null);
    }, []);

    useEffect(() => {
        if (!user) return;

        Intercom({
            app_id: 'lpyaa5lt',
            user_id: user.id,
            name: user.name,
            email: user.email
        });
    }, []);

    useEffect(() => {
        const handleClickOutside = e => {
            if (userRef.current && !userRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        document.body.classList.toggle('sidebar-collapsed', collapsed);
        return () => document.body.classList.remove('sidebar-collapsed');
    }, [collapsed]);

    useEffect(() => {
        setFlyout(null);
    }, [pathname, collapsed]);

    const toggleCollapsed = () => {
        const next = !collapsedPreference;

        setCollapsedPreference(next);
        setUserMenuOpen(false);

        try {
            localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0');
        } catch (error) {
            // Sem storage a preferência vale só para esta visita.
        }
    };

    const toggleGroup = item => {
        setOpenGroups(prev => {
            const next = new Set(prev);
            const isOpen = next.has(item.key) || isItemActive(pathname, item);

            if (isOpen) {
                next.delete(item.key);
                // O grupo da rota atual abre sozinho; fechar precisa vencer isso.
                next.add(`closed:${item.key}`);
            } else {
                next.delete(`closed:${item.key}`);
                next.add(item.key);
            }

            return next;
        });
    };

    const isGroupOpen = item => {
        if (openGroups.has(`closed:${item.key}`)) return false;
        return openGroups.has(item.key) || isItemActive(pathname, item);
    };

    const showFlyout = (item, element) => {
        if (!collapsed) return;

        clearTimeout(flyoutTimer.current);

        const rect = element.getBoundingClientRect();
        const top = item.subitems ? Math.max(8, rect.top - 8) : rect.top + rect.height / 2;

        setFlyout({ item, top, left: rect.right + 12 });
    };

    const scheduleHideFlyout = () => {
        clearTimeout(flyoutTimer.current);
        flyoutTimer.current = setTimeout(() => setFlyout(null), 160);
    };

    const keepFlyout = () => clearTimeout(flyoutTimer.current);

    const openProfile = () => {
        setUserMenuOpen(false);
        setShowProfile(true);
    };

    const openSubscription = () => {
        setUserMenuOpen(false);
        navigate('/assinatura');
        onClose?.();
    };

    const signOut = () => {
        logout();
        navigate('/login');
    };

    const gestaoSection = () => {
        if (!hasPermission(GESTAO_PERMISSIONS)) return null;

        const state = getGestaoState();

        if (state !== FEATURE_ENABLED) {
            return {
                key: 'gestao',
                label: 'Gestão',
                soon: state === FEATURE_COMING_SOON,
                items: [{
                    key: 'gestao-offer',
                    name: 'Financeiro e fiscal',
                    svg: 'circle-dollar-sign',
                    show: true,
                    state,
                    onLockedClick: () => setUpgradeFeature(GESTAO_FEATURE),
                }],
            };
        }

        const canViewFinance = hasPermission(['view_finance', 'manage_finance']);
        const canViewFiscal = hasPermission(['view_fiscal', 'manage_fiscal']);
        const canViewPurchases = hasPermission(['view_purchase_order', 'manage_purchase_order']);

        return {
            key: 'gestao',
            label: 'Gestão',
            items: [
                {
                    key: 'finance',
                    name: 'Financeiro',
                    svg: 'circle-dollar-sign',
                    show: canViewFinance,
                    subitems: [
                        { name: 'Visão geral', to: '/finance/overview' },
                        { name: 'Contas a receber', to: '/finance/receivables' },
                        { name: 'Contas a pagar', to: '/finance/payables' },
                        { name: 'Extrato', to: '/finance/statement' },
                        { name: 'Fluxo de caixa', to: '/finance/cash-flow' },
                        { name: 'Conciliação bancária', to: '/finance/reconciliation' },
                    ],
                },
                {
                    // A nota de entrada é documento fiscal do fornecedor, por
                    // isso mora aqui e não em Compras.
                    key: 'fiscal',
                    name: 'Fiscal',
                    svg: 'scale',
                    show: canViewFiscal || canViewPurchases,
                    subitems: [
                        { name: 'Notas fiscais', to: '/fiscal/documents', show: canViewFiscal },
                        { name: 'Notas de entrada', to: '/purchase-invoice/list', matches: ['/purchase-invoice/sefaz'], show: canViewPurchases },
                    ].filter(subitem => subitem.show),
                },
                {
                    key: 'purchases',
                    name: 'Compras',
                    svg: 'clipboard-list',
                    to: '/purchase-order/list',
                    show: canViewPurchases,
                },
                {
                    key: 'reports',
                    name: 'Relatórios',
                    svg: 'chart',
                    to: '/report',
                    matches: ['/finance/income-statement'],
                    show: canViewFinance,
                },
                {
                    key: 'gestao-settings',
                    name: 'Cadastros e ajustes',
                    svg: 'sliders-horizontal',
                    to: firstGestaoSettingsPath(),
                    matches: GESTAO_SETTINGS_TABS.map(tab => tab.to).concat('/fiscal/operations'),
                    show: !!firstGestaoSettingsPath(),
                },
            ],
        };
    };

    const getSections = () => [
        {
            key: 'start',
            items: [
                {
                    key: 'dashboard',
                    name: 'Dashboard',
                    svg: 'dashboard',
                    to: '/dashboard',
                    show: hasPermission(['view_related_dashboard', 'view_unrelated_dashboard']),
                },
            ],
        },
        {
            key: 'commercial',
            label: 'Comercial',
            items: [
                {
                    key: 'quotes',
                    name: 'Orçamentos',
                    svg: 'calculator',
                    to: '/quote/list',
                    alert: quoteCount,
                    show: hasPermission(['view_unrelated_budget', 'view_related_budget']),
                },
                {
                    key: 'orders',
                    name: 'Pedidos de venda',
                    svg: 'receipt-text',
                    to: '/order/list',
                    show: hasPermission(['view_unrelated_order', 'view_related_order']),
                },
                {
                    key: 'crm',
                    name: 'CRM',
                    svg: 'square-kanban',
                    to: '/crm',
                    show: true,
                    state: hasPlanFeature('crm') ? undefined : 'locked',
                    onLockedClick: () => setUpgradeFeature('crm'),
                },
                {
                    key: 'companies',
                    name: 'Empresas',
                    svg: 'company',
                    to: '/company/list',
                    show: true,
                },
                {
                    key: 'people',
                    name: 'Pessoas',
                    svg: 'people',
                    to: '/customer/list',
                    show: hasPermission(['view_unrelated_customer', 'view_related_customer']),
                },
            ],
        },
        {
            key: 'catalog',
            label: 'Catálogo',
            items: [
                {
                    key: 'products',
                    name: 'Produtos',
                    svg: 'product',
                    show: hasPermission(['view_product', 'view_color', 'view_category', 'view_tag', 'view_customization']),
                    subitems: [
                        { name: 'Produtos', to: '/product/list', show: hasPermission('view_product') },
                        { name: 'Categorias', to: '/category/list', show: hasPermission('view_category') },
                        { name: 'Cores', to: '/color/list', show: hasPermission('view_color') },
                        { name: 'Gravações', to: '/customization/list', show: hasPermission('view_customization') },
                        { name: 'Selos', to: '/tag/list', show: hasPermission('view_tag') },
                    ].filter(subitem => subitem.show),
                },
                {
                    key: 'credits',
                    name: 'Recursos com IA',
                    svg: 'sparkles',
                    to: '/credits',
                    show: hasPermission(['view_credits', 'manage_credits']),
                    new: true,
                },
            ],
        },
        {
            key: 'storefronts',
            label: 'Vitrines',
            items: [
                {
                    key: 'site',
                    name: 'Site',
                    svg: 'globe',
                    show: hasPermission('content'),
                    subitems: [
                        { name: 'Páginas', to: '/page/list' },
                        { name: 'Banners', to: '/banner/list' },
                        { name: 'Logos de clientes', to: '/logo/list' },
                        { name: 'Depoimentos', to: '/testimonial/list' },
                    ],
                },
                {
                    key: 'brands',
                    name: 'Marcas',
                    svg: 'brand',
                    to: '/brand/list',
                    show: hasPermission('view_brand'),
                    state: hasPlanFeature('brands') ? undefined : 'locked',
                    onLockedClick: () => setUpgradeFeature('brands'),
                    new: true,
                },
            ],
        },
        gestaoSection(),
        {
            key: 'settings',
            divider: true,
            items: [
                {
                    key: 'settings',
                    name: 'Configurações',
                    svg: 'config',
                    to: '/settings',
                    matches: SETTINGS_PATHS,
                    show: canSeeSettingsHub(),
                },
            ],
        },
    ]
        .filter(Boolean)
        .map(section => ({
            ...section,
            items: section.items.filter(item => item.show && (!item.subitems || item.subitems.length > 0)),
        }))
        .filter(section => section.items.length > 0);

    const sections = getSections();
    const roleName = user?.role?.name ?? 'Sem nível de acesso';

    return (
        <aside id="sidebar" className={`${mobileOpen ? 'mobile-open' : ''}${collapsed ? ' collapsed' : ''}`}>
            <button
                type="button"
                className="sidebar-close-button"
                aria-label="Fechar menu"
                onClick={onClose}
            >
                <Icon name="close" size={20} />
            </button>
            <div className="sidebar-brand">
                <Link to="/" className="brand-logo" onClick={onClose} aria-label="Ir para o início">
                    <BrandLogo />
                </Link>
                <button
                    type="button"
                    className="collapse-toggle"
                    onClick={toggleCollapsed}
                    aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
                    title={collapsed ? 'Expandir menu' : 'Recolher menu'}
                >
                    <Icon name="chevrons-left" size={18} />
                </button>
            </div>
            <nav id="menu" ref={menuRef} aria-label="Menu principal">
                <ul>
                    {sections.map(section => (
                        <React.Fragment key={section.key}>
                            {section.divider && <li className="divider" role="separator"></li>}
                            {section.label &&
                                <li className="section">
                                    {section.label}
                                    {section.soon && <span className="module-tag soon">Em breve</span>}
                                </li>
                            }
                            {section.items.map(item => (
                                <SidebarItem
                                    key={item.key}
                                    item={item}
                                    open={!!item.subitems && isGroupOpen(item)}
                                    onToggle={item => collapsed ? null : toggleGroup(item)}
                                    onNavigate={onClose}
                                    onHover={showFlyout}
                                    onLeave={scheduleHideFlyout}
                                />
                            ))}
                        </React.Fragment>
                    ))}
                </ul>
            </nav>

            <SidebarFlyout
                flyout={collapsed ? flyout : null}
                onNavigate={() => {
                    setFlyout(null);
                    onClose?.();
                }}
                onEnter={keepFlyout}
                onLeave={scheduleHideFlyout}
            />

            {credits.loaded && hasPermission(['view_credits', 'manage_credits']) &&
                <Link
                    to="/credits"
                    // Saldo zerado conta como baixo mesmo com o alerta
                    // desligado (low_balance_threshold = 0): sem crédito
                    // nenhum recurso de IA roda.
                    className={`credit-chip${credits.isLow || credits.balance <= 0 ? ' low' : ''}`}
                    onClick={onClose}
                    title={`Saldo de créditos${credits.reserved > 0 ? ` · ${credits.reserved} em uso agora` : ''}`}
                >
                    <CreditCoin size={16} />
                    <span className="value">{credits.balance.toLocaleString('pt-BR')}</span>
                    <span className="label">créditos</span>
                    <Icon name="arrow-right" className="go" size={13} />
                </Link>
            }

            <div id="user" ref={userRef}>
                <button
                    type="button"
                    className="user-identity"
                    onClick={() => setUserMenuOpen(prev => !prev)}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    title={collapsed ? `${user?.name ?? ''} · ${roleName}` : undefined}
                >
                    <Avatar name={user?.name} photo={user?.photo_url} size={40} />
                    <div className="user-texts">
                        <p className="name">{user?.name}</p>
                        <p className="role">{roleName}</p>
                    </div>
                    <Icon name="arrow-dropdown" className={`chevron${userMenuOpen ? ' open' : ''}`} size={16} />
                </button>

                <div className="user-tools">
                    <NotificationBell />
                </div>

                {userMenuOpen &&
                    <div className="user-menu" role="menu">
                        <div className="user-menu-info">
                            <p className="name">{user?.name}</p>
                            <p className="role">{roleName}</p>
                        </div>
                        <ul>
                            <li>
                                <button type="button" role="menuitem" onClick={openProfile}>
                                    <Icon name="user" size={18} /> Meus dados
                                </button>
                            </li>
                            <li>
                                <button type="button" role="menuitem" onClick={openSubscription}>
                                    <Icon name="credit-card" size={18} /> Assinatura
                                </button>
                            </li>
                            <li>
                                <a role="menuitem" href={KNOWLEDGE_BASE_URL} target="_blank" rel="noopener noreferrer" onClick={() => setUserMenuOpen(false)}>
                                    <Icon name="knowledgeBase" size={18} /> Central de ajuda
                                    <Icon name="external-link" size={14} className="ext" />
                                </a>
                            </li>
                            <li className="sep" role="separator"></li>
                            <li>
                                <button type="button" role="menuitem" onClick={signOut}>
                                    <Icon name="logout" size={18} /> Sair
                                </button>
                            </li>
                        </ul>
                    </div>
                }
            </div>

            <ModalMyProfile
                show={showProfile}
                close={() => setShowProfile(false)}
                onUpdated={() => setUserData(getUser())}
            />

            <PlanFeatureUpgradeModal
                feature={upgradeFeature}
                show={upgradeFeature !== null}
                close={() => setUpgradeFeature(null)}
            />
        </aside>
    );
}

export default Sidebar;
